import {
  BenefitPlatform,
  BenefitPlatformCreate,
  BenefitPlatformUpdate,
  createBenefitPlatform,
  listBenefitPlatformCredentials,
  listBenefitPlatforms,
  updateBenefitPlatform,
} from '../../api/benefitPlatformApi'
import { BenefitPlatformCredentialOption } from '../../api/benefitPlatformApi'
import NavBar from '../../components/nav-bar/nav-bar'
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  notification,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
} from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import { useCallback, useEffect, useState } from 'react'

type PlatformFormValues = Omit<BenefitPlatformCreate, 'subsidyValue'> & {
  subsidyValue?: number
  enabled: boolean
}

const toDisplaySubsidyValue = (platform: BenefitPlatform): number =>
  platform.subsidyType === 'FIXED_CENTS' ? platform.subsidyValue / 100 : platform.subsidyValue

const toStoredSubsidyValue = (type: BenefitPlatform['subsidyType'], value?: number): number =>
  type === 'NONE'
    ? 0
    : type === 'PERCENTAGE'
      ? Math.round(value ?? 0)
      : Math.round((value ?? 0) * 100)

// The API requires a positive base price even when the platform covers the full amount.
const defaultFullyCoveredCashCentsPerCredit = 100
const integrationTypes: BenefitPlatform['integrationType'][] = ['SANDBOX']

const BenefitPlatformsPage = () => {
  const [platforms, setPlatforms] = useState<BenefitPlatform[]>([])
  const [credentials, setCredentials] = useState<BenefitPlatformCredentialOption[]>([])
  const [editor, setEditor] = useState<BenefitPlatform | 'new' | null>(null)
  const [form] = Form.useForm<PlatformFormValues>()
  const subsidyType = Form.useWatch('subsidyType', form)
  const paymentMode = Form.useWatch('paymentMode', form)
  const [loading, setLoading] = useState(false)
  const availableIntegrationTypes = integrationTypes.filter(
    integrationType => !platforms.some(platform => platform.integrationType === integrationType),
  )

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      const [nextPlatforms, nextCredentials] = await Promise.all([
        listBenefitPlatforms(),
        listBenefitPlatformCredentials(),
      ])
      setPlatforms(nextPlatforms)
      setCredentials(nextCredentials)
    } catch (error: any) {
      notification.error({ message: '权益平台加载失败', description: error?.message })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    reload()
  }, [reload])

  const openEditor = (platform: BenefitPlatform | 'new') => {
    form.resetFields()
    form.setFieldsValue(
      platform === 'new'
        ? { subsidyType: 'NONE', subsidyValue: 0, paymentMode: 'USER_PAY', enabled: true }
        : { ...platform, subsidyValue: toDisplaySubsidyValue(platform) },
    )
    setEditor(platform)
  }

  const save = async () => {
    const values = await form.validateFields()
    const fullyCovered = values.paymentMode === 'FULLY_COVERED'
    const cashCentsPerCredit = fullyCovered
      ? editor !== 'new' && editor
        ? editor.cashCentsPerCredit
        : defaultFullyCoveredCashCentsPerCredit
      : values.cashCentsPerCredit
    const subsidyType = fullyCovered ? 'NONE' : values.subsidyType
    const subsidyValue = fullyCovered
      ? 0
      : toStoredSubsidyValue(values.subsidyType, values.subsidyValue)
    try {
      if (editor === 'new') {
        const create: BenefitPlatformCreate = {
          code: values.code,
          name: values.name,
          clientCredentialId: values.clientCredentialId,
          integrationType: values.integrationType,
          cashCentsPerCredit,
          subsidyType,
          subsidyValue,
          paymentMode: values.paymentMode,
        }
        await createBenefitPlatform(create)
      } else if (editor) {
        const update: BenefitPlatformUpdate = {
          name: values.name,
          clientCredentialId: values.clientCredentialId,
          cashCentsPerCredit,
          subsidyType,
          subsidyValue,
          paymentMode: values.paymentMode,
          enabled: values.enabled,
        }
        await updateBenefitPlatform(editor.id, update)
      }
      setEditor(null)
      await reload()
      notification.success({ message: '权益平台已保存' })
    } catch (error: any) {
      notification.error({ message: '权益平台保存失败', description: error?.message })
    }
  }

  return (
    <Space className="dashboard-wrapper" orientation="vertical">
      <NavBar title="权益平台管理" />
      <Card>
        <Space>
          <Button
            type="primary"
            disabled={availableIntegrationTypes.length === 0}
            onClick={() => openEditor('new')}
          >
            新增权益平台
          </Button>
          {availableIntegrationTypes.length === 0 && (
            <Tooltip title="所有对接类型均已创建权益平台，不能重复创建。">
              <InfoCircleOutlined />
            </Tooltip>
          )}
        </Space>
        <Table
          rowKey="id"
          loading={loading}
          dataSource={platforms}
          pagination={{ pageSize: 20 }}
          columns={[
            { title: '平台', dataIndex: 'name' },
            { title: 'Code', dataIndex: 'code' },
            { title: '对接类型', dataIndex: 'integrationType' },
            {
              title: '关联凭据',
              dataIndex: 'clientCredentialId',
              render: (id: number) =>
                `${credentials.find(credential => credential.id === id)?.name ?? '未知'} (#${id})`,
            },
            { title: '价格系数（分/点）', dataIndex: 'cashCentsPerCredit' },
            { title: '补贴类型', dataIndex: 'subsidyType' },
            { title: '支付模式', dataIndex: 'paymentMode' },
            {
              title: '状态',
              dataIndex: 'enabled',
              render: (enabled: boolean) => (
                <Tag color={enabled ? 'green' : 'default'}>{enabled ? '启用' : '停用'}</Tag>
              ),
            },
            {
              title: '操作',
              render: (_: unknown, platform: BenefitPlatform) => (
                <Button onClick={() => openEditor(platform)}>编辑</Button>
              ),
            },
          ]}
        />
      </Card>
      <Modal
        title={editor === 'new' ? '新增权益平台' : '编辑权益平台'}
        open={editor !== null}
        onCancel={() => setEditor(null)}
        onOk={save}
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          <Form.Item name="integrationType" label="对接类型" rules={[{ required: true }]}>
            <Select
              disabled={editor !== 'new'}
              options={(editor === 'new' ? availableIntegrationTypes : integrationTypes).map(
                integrationType => ({ label: integrationType, value: integrationType }),
              )}
            />
          </Form.Item>
          {editor === 'new' && (
            <Form.Item name="code" label="平台 Code" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
          )}
          <Form.Item
            name="clientCredentialId"
            label="Client Credential"
            rules={[{ required: true }]}
          >
            <Select
              options={credentials
                .filter(
                  credential =>
                    !credential.isBlocked &&
                    ((editor !== 'new' && editor?.clientCredentialId === credential.id) ||
                      !platforms.some(platform => platform.clientCredentialId === credential.id)),
                )
                .map(credential => ({
                  value: credential.id,
                  label: `${credential.name} (#${credential.id})`,
                }))}
            />
          </Form.Item>
          <Form.Item name="name" label="平台名称" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="paymentMode" label="支付模式" rules={[{ required: true }]}>
            <Select
              options={[
                { label: '用户支付余额', value: 'USER_PAY' },
                { label: '平台全额覆盖', value: 'FULLY_COVERED' },
              ]}
            />
          </Form.Item>
          {paymentMode === 'USER_PAY' && (
            <>
              <Form.Item
                name="cashCentsPerCredit"
                label="每点现金价格（分）"
                rules={[{ required: true }]}
              >
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="subsidyType" label="补贴类型" rules={[{ required: true }]}>
                <Select
                  onChange={(type: BenefitPlatform['subsidyType']) =>
                    form.setFieldValue('subsidyValue', type === 'NONE' ? 0 : undefined)
                  }
                  options={[
                    { label: '无补贴', value: 'NONE' },
                    { label: '比例', value: 'PERCENTAGE' },
                    { label: '固定金额', value: 'FIXED_CENTS' },
                  ]}
                />
              </Form.Item>
              {subsidyType && subsidyType !== 'NONE' && (
                <Form.Item
                  name="subsidyValue"
                  label={subsidyType === 'PERCENTAGE' ? '补贴比例' : '固定补贴金额'}
                  rules={[{ required: true, message: '请输入补贴数值' }]}
                >
                  <InputNumber
                    min={0}
                    max={subsidyType === 'PERCENTAGE' ? 100 : undefined}
                    precision={subsidyType === 'PERCENTAGE' ? 0 : 2}
                    step={subsidyType === 'PERCENTAGE' ? 1 : 0.01}
                    suffix={subsidyType === 'PERCENTAGE' ? '%' : '元'}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              )}
            </>
          )}
          {editor !== 'new' && (
            <Form.Item name="enabled" label="启用" valuePropName="checked">
              <Switch />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </Space>
  )
}

export default BenefitPlatformsPage
